import React from 'react';
import { get, set, sortBy, pick, size, isString, trim } from 'lodash';
import moment from 'moment';

export const chargerMode = { init: 'INIT', update: 'UPDATE' };
export const warrantyEndPath = 'c_exploitation_info.c_warranty_end';
const portNamesPath = 'c_i2p2_configuration.c_port_names';
const chargerModelPaths = [
  'c_identity.c_country',
  'c_identity.c_address',
  'c_identity.c_latitude',
  'c_identity.c_longitude',
  'c_identity.c_name',
  'c_identity.c_utc',
  'c_identity.vendor',
  'c_identity.picture_keys',
  'c_identity.product_serial_number',

  'c_i2p2_configuration.c_port_names',

  'i2p2_state.ports',
  'i2p2_state.c_condition',
  'i2p2_state.c_status',
  'i2p2_state.system_generated_values',

  'c_exploitation_info.c_owner',
  'c_exploitation_info.c_supervisor',
  'c_exploitation_info.c_maintainer',
  'c_exploitation_info.c_nickname',
  'c_exploitation_info.c_warranty_end',
  'c_exploitation_info.c_contact',
  'c_exploitation_info.notes',

  'c_technical_information.c_product_version',
  'c_technical_information.c_enclosure',
  'c_technical_information.c_product_level',
  'c_technical_information.c_chademo_cable',
  'c_technical_information.c_ac_hw_version',
  'c_technical_information.c_gir_reboot',

  'vendorData.vendor_serial',
  'vendorData.product_serial',
];

const calculateSinceDateString = (nowLocal, sinceLocal) => {
  let differenceString = '';
  const diff = nowLocal.diff(sinceLocal);
  const diffInHours = nowLocal.diff(sinceLocal, 'hours');
  const duration = moment.duration(diff);
  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.subtract(days, 'days').asHours());
  const minutes = Math.floor(duration.subtract(hours, 'hours').asMinutes());

  const minutesString = 'min';
  const hoursString = `${'h'}`;
  const daysString = `${days > 1 ? 'days' : 'day'}`;

  if (diffInHours < 1) {
    differenceString = `${minutes} ${minutesString}`;
  } else {
    if (diffInHours < 24) {
      differenceString = `${hours} ${hoursString} ${minutes} ${minutesString}`;
    } else {
      differenceString = `${days} ${daysString} ${hours} ${hoursString} ${minutes} ${minutesString}`;
    }
  }

  return differenceString;
};

export const getOfflineTimeInfo = (onlineTs) => {
  const since = moment.utc(onlineTs);
  const nowLocal = moment();
  const sinceLocal = moment(onlineTs);
  const differenceString = calculateSinceDateString(nowLocal, sinceLocal);

  return (
    <>
      <span>{`since ${since.format('L LTS')} UTC, ${differenceString}`}</span>
    </>
  );
};

class ChargerPreprocessor {
  process(charger) {
    const processed = { ...charger };

    this.sortConnectorsById(processed);
    this.processData(processed, chargerMode.init);
    this.processConnectorsModulesErrors(processed);
    this.processConnectorsErrors(processed);
    this.addConnector0Name(processed);

    return processed;
  }

  sortConnectorsById(charger) {
    const sorter = (path) => {
      const connectors = get(charger, path);

      if (connectors) {
        const sorted = sortBy(connectors, ['connector_id']);

        set(charger, path, sorted);
      }
    };

    sorter('i2p2_state.ports');
    sorter('c_full_state');
  }

  addConnector0Name(charger) {
    const firstPortValue = get(charger, 'c_i2p2_configuration.c_port_names[0]');

    if (!firstPortValue) {
      set(charger, 'c_i2p2_configuration.c_port_names[0]', 'Station');
    }
  }

  processConnectorsModulesErrors(charger) {
    this.connectorIterator.call(charger, (connector) => {
      if (connector.vendor && connector.vendor.length > 0) {
        connector.vendor.forEach((module) => {
          // If module status field contains error check that we have error code and concatenate it with status
          // const error = _.size(module.module_status) && module.module_status.toLowerCase().includes('error') && get(module, 'v_values.module_error')
          // Dirty as homeless underwear hack
          const error =
            size(module.module_status) && module.module_status.toLowerCase() === 'internalerror' && get(module, 'v_values.module_error');

          if (error) {
            module.module_status = `${module.module_status}(${error})`;
          }
        });
      }
    });
  }

  processConnectorsErrors(charger) {
    this.connectorIterator.call(charger, (connector) => {
      let errors = false;

      if (connector.vendor && connector.vendor.length > 0) {
        connector.vendor.forEach((module) => {
          if (
            size(module.module_status) &&
            module.module_status.toLowerCase().includes('error') &&
            module.module_name.toLowerCase() !== 'monitoring_link'
          ) {
            errors = true;
          }
        });
      }
      set(connector, 'module_errors', errors); // Hotfix. Should come from backend
    });
  }

  connectorIterator(callback) {
    if (this.c_full_state.length > 0) {
      this.c_full_state.forEach(callback);
    }
  }

  fillPortNames(data) {
    const ifPortNamesValid = (port) => isString(port) && trim(port) !== '';
    const portNames = get(data, portNamesPath);
    const processedPortNames = size(portNames) ? portNames.map((port) => (ifPortNamesValid(port) ? port : null)) : [];

    set(data, portNamesPath, processedPortNames);
  }

  fillVendor(data, mode) {
    switch (mode) {
      case chargerMode.init:
        this.initVendor(data);
        break;
      case chargerMode.update:
        this.fillVendorForSaving(data);
        break;
      default:
        console.log('Unknown mode');
    }
  }

  initVendor(data) {
    const currVendorData = get(data, 'c_identity.vendor[0]');

    data.vendorData = {};
    if (currVendorData && currVendorData.v_values) {
      data.vendorData.vendor_serial = currVendorData.v_values.vendor_serial;
      data.vendorData.product_serial = currVendorData.v_values.product_serial;
    }
  }

  fillVendorForSaving(data) {
    const vendorSerial = get(data, 'vendorData.vendor_serial');
    const productSerial = get(data, 'vendorData.product_serial');
    let vendorObj = [];

    if (productSerial || vendorSerial) {
      const blankVendorObj = {
        v_values: {},
      };

      if (productSerial) blankVendorObj.v_values.product_serial = productSerial;
      if (vendorSerial) blankVendorObj.v_values.vendor_serial = vendorSerial;
      vendorObj = [blankVendorObj];
    }
    set(data, 'c_identity.vendor', vendorObj);
  }

  getChargerSavingModel(chargerBody) {
    const filtered = pick(chargerBody, chargerModelPaths);

    if (!get(filtered, 'c_exploitation_info')) set(filtered, 'c_exploitation_info', {});
    if (!get(filtered, 'c_technical_information')) set(filtered, 'c_technical_information', {});
    if (!get(filtered, 'c_exploitation_info.c_contact')) set(filtered, 'c_exploitation_info.c_contact', null);
    if (!get(filtered, 'c_exploitation_info.c_nickname')) set(filtered, 'c_exploitation_info.c_nickname', null);
    if (!get(filtered, 'i2p2_state.system_generated_values')) set(filtered, 'i2p2_state.system_generated_values', []);

    this.processData(filtered, chargerMode.update);

    return filtered;
  }

  processData(data, mode) {
    this.fillPortNames(data);
    this.fillVendor(data, mode);
  }
}

export default new ChargerPreprocessor();
